import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { EVENTS_ENABLED } from '../constants/featureFlags';
import { EVENTS_CHANGED_EVENT, loadEvents, saveEvents, type StoredEvent } from '../services/EventsService';
import {
  createOrReplaceUserProfile,
  deleteUserProfile as deleteRemoteUserProfile,
  getUserProfile,
  patchUserProfile,
  type OnboardingProfileData,
  type UserProfile,
  setProfileUserEmail,
} from '../services/UserProfileService';
import {
  loadProjectChoices,
  saveProjectChoices as saveProjectChoicesRemote,
  NO_PROJECT_CHOICES,
  type ProjectChoices,
  type ProjectChoicesChange,
} from '../services/SettingsService';
import { makeMoney, type ProjectCurrency } from '../utils/currency';
import { setAppLocale } from '../utils/locale';
import { sanitizeHiddenMenu } from '../utils/sidebarMenu';

const SETTINGS_STORAGE_KEY = 'user_settings';
const ONBOARDING_COMPLETE_KEY = 'anojeje_onboarding_complete';
const PROFILE_OWNER_KEY = 'anojeje_profile_owner_email';

const ONBOARDING_LOCAL_KEYS = {
  hashtag: 'anojeje_hashtag',
  instagram: 'anojeje_instagram',
  guestCapacity: 'anojeje_guest_capacity',
  budgetCeiling: 'anojeje_budget_ceiling',
  city: 'anojeje_onboarding_city',
  sheetsId: 'anojeje_sheets_id',
} as const;

export interface SettingsState {
  theme: 'light' | 'dark' | 'auto';
  language: 'en' | 'fr' | 'es' | 'pt';
  /** The project's primary currency; '' until one is chosen. */
  currency: ProjectCurrency;
  notifications: {
    email: boolean;
    push: boolean;
    sms: boolean;
  };
  privacy: {
    profileVisibility: 'public' | 'private' | 'friends';
    dataSharing: boolean;
    analytics: boolean;
  };
  /** Left-hand menu: ids of the entries this user hid (Dashboard and Settings are never hidden). */
  menu: {
    hidden: string[];
  };
  /** The project's own settings block. Profiles saved before the rename kept it under `wedding`; mergeSettings reads that too. */
  project: {
    coupleNames: string;
    rsvpDeadline: string;
    defaultBudgetCurrency: ProjectCurrency;
    autoSave: boolean;
    reminderDays: number;
    brideName: string;
    groomName: string;
    hashtag: string;
    mealPreferences: string[];
    entertainmentOptions: string[];
  };
}

interface SettingsContextType {
  settings: SettingsState;
  updateSettings: (newSettings: Partial<SettingsState>) => void;
  getCurrencySymbol: (currency?: string) => string;
  formatCurrency: (amount: number, currency?: string) => string;
  formatCurrencyCompact: (amount: number, currency?: string) => string;
  onboardingCompleted: boolean;
  onboardingData: OnboardingProfileData;
  isProfileLoading: boolean;
  completeOnboarding: (input: { settings: SettingsState; events: StoredEvent[]; onboardingData: OnboardingProfileData }) => Promise<void>;
  deleteUserProfile: () => Promise<void>;
  /**
   * What this project has chosen for currency, language, timezone and phone
   * country. Nothing is defaulted: an unchosen field is '' and is listed in
   * `missing`. The currency in `settings` always comes from here.
   */
  projectChoices: ProjectChoices;
  /** True once the API has answered at least once for this project. */
  projectChoicesLoaded: boolean;
  projectChoicesError: unknown | null;
  refreshProjectChoices: () => Promise<void>;
  saveProjectChoices: (change: ProjectChoicesChange) => Promise<ProjectChoices>;
}

const defaultSettings: SettingsState = {
  theme: 'light',
  language: 'en',
  currency: '',
  notifications: {
    email: true,
    push: true,
    sms: false,
  },
  privacy: {
    profileVisibility: 'private',
    dataSharing: false,
    analytics: true,
  },
  menu: {
    hidden: [],
  },
  project: {
    coupleNames: '',
    rsvpDeadline: '',
    defaultBudgetCurrency: '',
    autoSave: true,
    reminderDays: 7,
    brideName: '',
    groomName: '',
    hashtag: '',
    mealPreferences: [],
    entertainmentOptions: [],
  },
};

const defaultOnboardingData: OnboardingProfileData = {
  hashtag: '',
  instagram: '',
  guestCapacity: '',
  budgetCeiling: '',
  city: '',
  sheetsId: '',
};

/** A saved settings object: the current shape, or one that still has the project block under `wedding`. */
type SavedSettings = Partial<SettingsState> & { wedding?: Partial<SettingsState['project']> };

function mergeSettings(base: SettingsState, saved: SavedSettings): SettingsState {
  const { wedding: legacyProject, ...updates } = saved;
  return {
    ...base,
    ...updates,
    notifications: {
      ...base.notifications,
      ...(updates.notifications ?? {}),
    },
    privacy: {
      ...base.privacy,
      ...(updates.privacy ?? {}),
    },
    menu: {
      hidden: sanitizeHiddenMenu(updates.menu?.hidden ?? base.menu?.hidden),
    },
    project: {
      ...base.project,
      ...(legacyProject ?? {}),
      ...(updates.project ?? {}),
    },
  };
}


function persistLocalOnboardingData(data: Partial<OnboardingProfileData>) {
  localStorage.setItem(ONBOARDING_LOCAL_KEYS.hashtag, data.hashtag || '');
  localStorage.setItem(ONBOARDING_LOCAL_KEYS.instagram, data.instagram || '');
  localStorage.setItem(ONBOARDING_LOCAL_KEYS.guestCapacity, data.guestCapacity || '');
  localStorage.setItem(ONBOARDING_LOCAL_KEYS.budgetCeiling, data.budgetCeiling || '');
  localStorage.setItem(ONBOARDING_LOCAL_KEYS.city, data.city || '');
  if (data.sheetsId) {
    localStorage.setItem(ONBOARDING_LOCAL_KEYS.sheetsId, data.sheetsId);
  } else {
    localStorage.removeItem(ONBOARDING_LOCAL_KEYS.sheetsId);
  }
}

function persistLocalSettings(settings: SettingsState) {
  localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
}

function persistLocalEvents(events: StoredEvent[]) {
  saveEvents(events, { silent: true });
}

function clearLocalProfileState() {
  localStorage.removeItem(SETTINGS_STORAGE_KEY);
  localStorage.removeItem(ONBOARDING_COMPLETE_KEY);
  localStorage.removeItem(PROFILE_OWNER_KEY);
  localStorage.removeItem('anojeje_events');
  Object.values(ONBOARDING_LOCAL_KEYS).forEach(key => localStorage.removeItem(key));
}

export const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const { isAuthenticated, isLoading: isAuthLoading, user } = useAuth();
  const [settings, setSettings] = useState<SettingsState>(defaultSettings);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [onboardingData, setOnboardingData] = useState<OnboardingProfileData>(defaultOnboardingData);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [hasRemoteProfile, setHasRemoteProfile] = useState(false);
  const [projectChoices, setProjectChoices] = useState<ProjectChoices>(NO_PROJECT_CHOICES);
  const [projectChoicesLoaded, setProjectChoicesLoaded] = useState(false);
  const [projectChoicesError, setProjectChoicesError] = useState<unknown | null>(null);

  const applyProfile = useCallback((profile: UserProfile) => {
    const mergedSettings = mergeSettings(defaultSettings, profile.settings ?? {});
    const mergedOnboardingData = {
      ...defaultOnboardingData,
      ...(profile.onboarding ?? {}),
    };

    setSettings(mergedSettings);
    setOnboardingCompleted(profile.onboardingCompleted);
    setOnboardingData(mergedOnboardingData);
    persistLocalSettings(mergedSettings);
    persistLocalOnboardingData(mergedOnboardingData);
    // Only overwrite local events when the remote profile actually carries them.
    // Events are now the source-of-truth in GuestGuru DB and loaded via API; an
    // empty array in the profile just means they were never synced there — we
    // must NOT wipe whatever the API has already cached locally.
    // With the Events module on, the API is the only source: a stale copy in the profile must not replace it.
    if (!EVENTS_ENABLED && (profile.events ?? []).length > 0) {
      persistLocalEvents(profile.events);
    }
  }, []);

  const persistProfile = useCallback(async (payload: {
    settings?: SettingsState;
    events?: StoredEvent[];
    onboarding?: OnboardingProfileData;
    onboardingCompleted?: boolean;
  }) => {
    if (!isAuthenticated || !user) {
      return;
    }

    const resolvedSettings = payload.settings ?? settings;
    const resolvedEvents = payload.events ?? loadEvents();
    const resolvedOnboarding = payload.onboarding ?? onboardingData;
    const resolvedOnboardingCompleted = payload.onboardingCompleted ?? onboardingCompleted;

    if (hasRemoteProfile) {
      await patchUserProfile({
        settings: payload.settings,
        events: payload.events,
        onboarding: payload.onboarding,
        onboardingCompleted: payload.onboardingCompleted,
      });
      return;
    }

    const createdProfile = await createOrReplaceUserProfile({
      onboardingCompleted: resolvedOnboardingCompleted,
      settings: resolvedSettings,
      events: resolvedEvents,
      onboarding: resolvedOnboarding,
    });
    applyProfile(createdProfile);
    setHasRemoteProfile(true);
  }, [applyProfile, hasRemoteProfile, isAuthenticated, onboardingCompleted, onboardingData, settings, user]);

  useEffect(() => {
    let cancelled = false;

    const hydrateProfile = async () => {
      if (isAuthLoading) {
        // Auth hasn't resolved yet — keep the loading state up so the app
        // doesn't flash the onboarding wizard before we know who's logged in.
        setIsProfileLoading(true);
        return;
      }

      if (!isAuthenticated || !user) {
        setSettings(defaultSettings);
        setOnboardingCompleted(false);
        setOnboardingData(defaultOnboardingData);
        setHasRemoteProfile(false);
        setIsProfileLoading(false);
        return;
      }

      setIsProfileLoading(true);
      setProfileUserEmail(user.email);

      try {
        const profile = await getUserProfile();
        if (cancelled) return;

        // The setup wizard is hidden until events return (a later wave); an
        // empty project is asked for its settings instead (ProjectSetupPrompt).
        // So there is no "is this project already configured" check against
        // the events route any more.
        if (profile) {
          applyProfile(profile);
          setHasRemoteProfile(true);
          return;
        }

        // No saved profile for this person yet.
        setHasRemoteProfile(false);
      } catch (error) {
        console.error('Error loading user profile:', error);
        setSettings(defaultSettings);
        setOnboardingData(defaultOnboardingData);
        setOnboardingCompleted(false);
        setHasRemoteProfile(false);
      } finally {
        if (!cancelled) {
          setIsProfileLoading(false);
        }
      }
    };

    void hydrateProfile();

    return () => {
      cancelled = true;
    };
  }, [applyProfile, isAuthLoading, isAuthenticated, user]);

  const updateSettings = useCallback((newSettings: Partial<SettingsState>) => {
    setSettings(prev => {
      const merged = mergeSettings(prev, newSettings);
      persistLocalSettings(merged);
      void persistProfile({ settings: merged }).catch(error => {
        console.error('Error saving settings to profile:', error);
      });
      return merged;
    });
  }, [persistProfile]);

  useEffect(() => {
    if (!isAuthenticated || isProfileLoading) {
      return;
    }

    const handleEventsChanged = (event: Event) => {
      if (EVENTS_ENABLED) return; // events live in the API; nothing is copied into the profile
      const customEvent = event as CustomEvent<StoredEvent[]>;
      const events = customEvent.detail ?? loadEvents();
      void persistProfile({ events }).catch(error => {
        console.error('Error saving events to profile:', error);
      });
    };

    window.addEventListener(EVENTS_CHANGED_EVENT, handleEventsChanged as EventListener);
    return () => {
      window.removeEventListener(EVENTS_CHANGED_EVENT, handleEventsChanged as EventListener);
    };
  }, [isAuthenticated, isProfileLoading, persistProfile]);

  const completeOnboarding = useCallback(async ({ settings: nextSettings, events, onboardingData: nextOnboardingData }: { settings: SettingsState; events: StoredEvent[]; onboardingData: OnboardingProfileData }) => {
    const profile = await createOrReplaceUserProfile({
      onboardingCompleted: true,
      settings: nextSettings,
      events,
      onboarding: nextOnboardingData,
    });

    applyProfile(profile);
    setHasRemoteProfile(true);
  }, [applyProfile]);

  const deleteUserProfile = useCallback(async () => {
    await deleteRemoteUserProfile();
    clearLocalProfileState();
    setSettings(defaultSettings);
    setOnboardingCompleted(false);
    setOnboardingData(defaultOnboardingData);
    setHasRemoteProfile(false);
  }, []);

  // The project's own choices (currency, language, timezone, phone country).
  const refreshProjectChoices = useCallback(async () => {
    try {
      const choices = await loadProjectChoices();
      setProjectChoices(choices);
      setProjectChoicesLoaded(true);
      setProjectChoicesError(null);
    } catch (error) {
      setProjectChoicesError(error);
    }
  }, []);

  useEffect(() => {
    if (isAuthLoading) return;
    if (!isAuthenticated || !user) {
      setProjectChoices(NO_PROJECT_CHOICES);
      setProjectChoicesLoaded(false);
      setProjectChoicesError(null);
      return;
    }
    void refreshProjectChoices();
  }, [isAuthLoading, isAuthenticated, user, refreshProjectChoices]);

  const saveProjectChoices = useCallback(async (change: ProjectChoicesChange) => {
    const choices = await saveProjectChoicesRemote(change);
    setProjectChoices(choices);
    setProjectChoicesLoaded(true);
    setProjectChoicesError(null);
    return choices;
  }, []);

  // The currency always comes from the project. A saved per-person profile may
  // still remember an old one; it is never used, so a project that has not
  // chosen a currency says "currency not set" instead of guessing.
  const effectiveSettings = useMemo<SettingsState>(() => ({
    ...settings,
    currency: projectChoices.currency,
    project: { ...settings.project, defaultBudgetCurrency: projectChoices.currency },
  }), [settings, projectChoices.currency]);

  // Numbers and dates follow the project's language, else the setting chosen in the profile; never a country baked into the code.
  useEffect(() => {
    setAppLocale(projectChoices.locale || settings.language);
  }, [projectChoices.locale, settings.language]);

  // No fallback currency: when the project has none, amounts say so.
  const money = makeMoney(projectChoices.currency);
  const getCurrencySymbol = (currency?: string): string => (currency ? makeMoney(currency).symbol : money.symbol);
  const formatCurrency = (amount: number, currency?: string): string => money.format(amount, currency);
  const formatCurrencyCompact = (amount: number, currency?: string): string => money.formatCompact(amount, currency);

  const value = {
    settings: effectiveSettings,
    updateSettings,
    getCurrencySymbol,
    formatCurrency,
    formatCurrencyCompact,
    onboardingCompleted,
    onboardingData,
    isProfileLoading,
    completeOnboarding,
    deleteUserProfile,
    projectChoices,
    projectChoicesLoaded,
    projectChoicesError,
    refreshProjectChoices,
    saveProjectChoices,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
